import { CommonModule } from '@angular/common';
import { Component, DestroyRef, Input, OnInit, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router, RouterModule } from '@angular/router';

import { DxFormModule } from 'devextreme-angular/ui/form';
import { DxLoadIndicatorModule } from 'devextreme-angular/ui/load-indicator';
import { DxButtonModule, DxButtonTypes } from 'devextreme-angular/ui/button';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { AuthErrorType, AuthService, IResponse, ThemeService } from 'src/app/services';

@Component({
  selector: 'app-login-form',
  templateUrl: './login-form.component.html',
  styleUrls: ['./login-form.component.scss'],
  imports: [
    CommonModule,
    RouterModule,
    DxFormModule,
    DxLoadIndicatorModule,
    DxButtonModule,
    TranslateModule,
  ]
})
export class LoginFormComponent implements OnInit {
  @Input() resetLink = '/auth/reset-password';

  @Input() createAccountLink = '/auth/register';

  private authService = inject(AuthService);

  private router = inject(Router);

  private themeService = inject(ThemeService);

  private translate = inject(TranslateService);

  private destroyRef = inject(DestroyRef);

  defaultAuthData: IResponse;

  btnStylingMode: DxButtonTypes.ButtonStyle;

  loading = false;

  formData: any = {};

  errorMessage: string | null = null;

  errorType: AuthErrorType | null = null;

  // Referencia interna al editor para poder togglear el mode desde el botón ojito
  // y para refrescar el placeholder al cambiar de idioma.
  private passwordTextBox: any = null;
  // Tracker del estado mostrar/ocultar — necesario para que al cambiar idioma el
  // hint refleje el estado correcto (sin esto re-haríamos el editor "show" cuando
  // estaba en "hide").
  private passwordVisible = false;

  /** Editor options reactivos — se reemplazan en cada cambio de idioma. DevExtreme
   *  rebina cuando la referencia del objeto cambia, así que un nuevo objeto fuerza
   *  el re-render con los textos traducidos. */
  emailEditorOptions: any = {};
  passwordEditorOptions: any = {};
  rememberMeEditorOptions: any = {};

  constructor() {
    this.themeService.isDark.subscribe((value: boolean) => {
      this.btnStylingMode = value ? 'outlined' : 'contained';
    });

    // Build inicial de editor options + suscripción a cambios de idioma.
    this.buildEditorOptions();
    this.translate.onLangChange
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.buildEditorOptions());
  }

  /**
   * Construye los editor options usando `translate.instant()`. Se vuelve a llamar
   * cada vez que cambia el idioma — la referencia nueva del objeto fuerza a
   * DevExtreme a aplicar los textos traducidos sin recrear el editor entero.
   */
  private buildEditorOptions(): void {
    this.emailEditorOptions = {
      stylingMode: 'filled',
      placeholder: this.translate.instant('auth.usuarioPlaceholder'),
      value: this.defaultAuthData?.data?.email ?? '',
    };

    this.passwordEditorOptions = {
      placeholder: this.translate.instant('auth.contrasena'),
      stylingMode: 'filled',
      mode: this.passwordVisible ? 'text' : 'password',
      value: this.formData?.password ?? '',
      onInitialized: (e: any) => {
        this.passwordTextBox = e.component;
      },
      buttons: [
        {
          name: 'togglePasswordVisibility',
          location: 'after',
          options: {
            icon: this.passwordVisible ? 'eyeclose' : 'eyeopen',
            stylingMode: 'text',
            hint: this.translate.instant(
              this.passwordVisible ? 'auth.ocultarContrasena' : 'auth.mostrarContrasena',
            ),
            elementAttr: { class: 'pwd-visibility-btn' },
            onClick: (btnEvent: any) => {
              if (!this.passwordTextBox) return;
              this.passwordVisible = !this.passwordVisible;
              this.passwordTextBox.option('mode', this.passwordVisible ? 'text' : 'password');
              btnEvent.component.option('icon', this.passwordVisible ? 'eyeclose' : 'eyeopen');
              btnEvent.component.option(
                'hint',
                this.translate.instant(
                  this.passwordVisible ? 'auth.ocultarContrasena' : 'auth.mostrarContrasena',
                ),
              );
            },
          },
        },
      ],
    };

    this.rememberMeEditorOptions = {
      text: this.translate.instant('auth.recordarmeEnDispositivo'),
      elementAttr: { class: 'form-text' },
    };
  }

  async onSubmit(e: Event) {
    e.preventDefault();
    const { email, password, rememberMe } = this.formData;
    this.loading = true;
    this.dismissError();

    const result = await this.authService.logIn(email, password, !!rememberMe);
    if (!result.isOk) {
      // Solo quitamos el spinner en error. En exito, el `await logIn()` ya
      // espero la navegacion (ver auth.service.ts) y el componente se va a
      // desmontar al cambiar de ruta — mantener loading=true evita el flash
      // del form vacio que se veia en IIS prod por el chunk lazy de /home.
      this.loading = false;
      this.errorMessage = result.message ?? this.translate.instant('common.errorGenerico');
      this.errorType = result.errorType ?? 'unknown';
    }
  }

  dismissError() {
    this.errorMessage = null;
    this.errorType = null;
  }

  onCreateAccountClick = () => {
    this.router.navigate([this.createAccountLink]);
  };

  async ngOnInit(): Promise<void> {
    this.defaultAuthData = await this.authService.getUser();
    // Re-bind del email editor con el value cargado (si lo había).
    this.buildEditorOptions();
  }
}
